import { FacilityScreenRow } from 'app/models/facility-screen-row'
import { createReducer } from 'app/state/utils'
import {
  acceptedIds,
  invalidate,
  oneId,
  outdated,
  withRows,
  withStatus,
  withTermination,
} from 'app/state/utils/generation'
import * as facilityTypes from 'app/state/ducks/facilities/types'
import * as dealTypes from 'app/state/ducks/deals/types'
import * as entityTypes from 'app/state/ducks/entities/types'
import * as amendmentTypes from 'app/state/ducks/facility-amendments/types'
import * as types from './types'

export interface FacilityScreenState {
  rows: FacilityScreenRow[]
  isLoading: boolean
  /**
   * True once a write has happened that the rows cannot be patched for, or a
   * read came back from before a write. The thunk reads once more for the
   * second; the grid reloads once, while on screen, for the first.
   */
  stale: boolean
  /**
   * Bumped by every write the store learns of. A request records the
   * generation it started under; a response from an older generation is
   * thrown away, because it predates a write the rows already reflect
   * (utils/generation.ts).
   */
  generation: number
  /**
   * Entity id → name and deal id → name, from the entity and deal lists as
   * they pass through the store (the facility form loads both when it opens).
   * A saved facility's admin and deal are named from them, so a name on no
   * other row still shows without a reload. Not writes: they never move the
   * generation.
   */
  entityNames: Record<string, string>
  dealNames: Record<string, string>
}

const initialState: FacilityScreenState = {
  rows: [],
  isLoading: false,
  stale: false,
  generation: 0,
  entityNames: {},
  dealNames: {},
}

type Row = FacilityScreenRow

/**
 * The names the statement resolved server-side, read back off the rows, and
 * the entity and deal lists' names on top (fresher: the form that saved the
 * facility loaded them on open), so a saved facility is shown with its deal
 * and admin named without another request.
 */
const namesOnGrid = (rows: Row[]) => {
  const deals = new Map<string, string>()
  const entities = new Map<string, string>()
  for (const row of rows) {
    if (row.dealId?.id && row.dealName) deals.set(row.dealId.id, row.dealName)
    if (row.adminEntity?.id && row.adminEntityName) {
      entities.set(row.adminEntity.id, row.adminEntityName)
    }
  }
  return { deals, entities }
}

/**
 * The fields of a saved record (the whole facility the create / edit responses
 * carry, or an amendment record) that the grid renders.
 */
const knownNames = (state: FacilityScreenState) => {
  const { deals, entities } = namesOnGrid(state.rows)
  for (const [id, name] of Object.entries(state.dealNames)) deals.set(id, name)
  for (const [id, name] of Object.entries(state.entityNames)) entities.set(id, name)
  return { deals, entities }
}

const fieldsOf = (saved: any, state: FacilityScreenState, row?: Row) => {
  const names = knownNames(state)
  // The edit thunks dispatch the form's REQUEST body, which carries only what
  // the form edits: a field it lacks (the key admin, the utilized amount, the
  // deal's full key) is kept from the row, never blanked — the bulk endpoints
  // address a facility by its key admin.
  const dealId = saved.dealId?.id ?? row?.dealId?.id ?? ''
  const dealAdmin = saved.dealId?.admin ?? row?.dealId?.admin
  const adminEntityId = saved.adminEntity?.id ?? row?.adminEntity?.id ?? null
  const amortizationScheduleId =
    saved.amortizationScheduleId?.id ?? row?.amortizationScheduleId?.id
  return {
    name: saved.name ?? row?.name ?? '',
    facilityType: saved.facilityType ?? row?.facilityType ?? '',
    amount: saved.amount ?? row?.amount ?? null,
    utilizedAmount: saved.utilizedAmount ?? row?.utilizedAmount ?? '0',
    baseCurrency: saved.baseCurrency ?? row?.baseCurrency ?? '',
    startDate: saved.startDate ?? row?.startDate ?? null,
    maturity: saved.maturity ?? row?.maturity ?? null,
    dealId: dealAdmin ? { id: dealId, admin: dealAdmin } : { id: dealId },
    dealName: names.deals.get(dealId) ?? '',
    adminEntity: adminEntityId ? { id: adminEntityId } : null,
    adminEntityName: adminEntityId ? (names.entities.get(adminEntityId) ?? '') : '',
    clientId: saved.clientId ?? row?.clientId ?? '',
    accountManagementAdmin:
      saved.accountManagementAdmin ?? row?.accountManagementAdmin ?? null,
    amortizationScheduleId: amortizationScheduleId
      ? { id: amortizationScheduleId }
      : null,
  }
}

/** The saved record in a create / edit action, if the response carried one. */
const savedRecord = (action: { payload?: any }) => {
  const saved = action.payload
  return saved?.id && saved?.dealId?.id ? saved : undefined
}

/** The values an amendment row carries of its approved facility. */
const approvedValuesOf = (row: Row): Row['approved'] => ({
  name: row.name,
  facilityType: row.facilityType,
  amount: row.amount,
  utilizedAmount: row.utilizedAmount,
  baseCurrency: row.baseCurrency,
  startDate: row.startDate,
  maturity: row.maturity,
  clientId: row.clientId,
  // an amendment may name another admin; deleting it brings this one back
  adminEntity: row.adminEntity,
  adminEntityName: row.adminEntityName,
})

/** A brand-new draft, as the statement would have returned it. */
const draftRow = (saved: any, state: FacilityScreenState): Row => ({
  id: saved.id,
  status: 'Draft',
  amendment: false,
  amendmentType: null,
  amendmentDate: null,
  hasAmendmentLenders: false,
  systemId: '',
  holds: [],
  hasNonProRataLoans: false,
  terminationDate: null,
  approved: null,
  ...fieldsOf(saved, state),
})

/**
 * The approved facility shows AS its amendment while one is in flight: the
 * amendment's values, `amendment: true`, its type, date, lenders flag and own
 * id, and the approved facility's own values kept under `approved`. Creating
 * an amendment therefore replaces the approved row; approving one makes its
 * values the facility's, deleting one brings the approved values back.
 */
const asAmendment = (row: Row, saved: any, state: FacilityScreenState): Row => ({
  ...row,
  ...fieldsOf(saved, state, row),
  // the amendment's deal and admin are the facility's
  dealName: row.dealName || dealNameOf(state, saved),
  amendment: true,
  status: saved.status === 'Submitted' ? 'Submitted' : 'Draft',
  transactionId: saved.transactionId ?? row.transactionId,
  amendmentType: saved.amendmentType ?? null,
  amendmentDate: saved.amendmentDate ?? null,
  hasAmendmentLenders:
    Array.isArray(saved.amendmentLenders) && saved.amendmentLenders.length > 0,
  approved: row.amendment ? row.approved : approvedValuesOf(row),
})
const dealNameOf = (state: FacilityScreenState, saved: any) =>
  knownNames(state).deals.get(saved.dealId?.id ?? '') ?? ''

/** Approved: the amendment's values are the facility's now; a termination locks it. */
const asApprovedFacility = (row: Row, status: Row['status']): Row => ({
  ...row,
  amendment: false,
  status,
  transactionId: undefined,
  amendmentType: null,
  amendmentDate: null,
  hasAmendmentLenders: false,
  approved: null,
})

/** Deleted: the approved facility's own values come back. */
const asRestoredFacility = (row: Row): Row => ({
  ...asApprovedFacility(row, 'Approved'),
  ...(row.approved ?? {}),
})

const isTermination = (row: Row) => row.amendmentType?.toLowerCase() === 'termination'

/** A termination created from the row menu: the answer carries the amendment's own id, not its record. */
const asTermination = (row: Row, amendmentId?: string): Row => ({
  ...row,
  amendment: true,
  status: 'Draft',
  transactionId: amendmentId,
  amendmentType: 'Termination',
  // the request's date is not in the answer; nothing on the grid shows it
  amendmentDate: null,
  hasAmendmentLenders: false,
  approved: row.amendment ? row.approved : approvedValuesOf(row),
})

/** Created or edited: the approved row shows as the amendment. */
const applyAmendmentRecord = (state: FacilityScreenState, action: any) => {
  const saved = savedRecord(action)
  if (!saved) return invalidate(state)
  return withRows(
    state,
    state.rows.map(row =>
      row.id === saved.id ? asAmendment(row, saved, state) : row,
    ),
  )
}

/**
 * The amendment list read (GET_FACILITY_AMENDMENTS): a termination created
 * from the row menu answers with no record, and its form reloads this list
 * instead. An amendment in it whose facility is still shown as a facility
 * becomes the amendment row, from the record. Not a write: the generation
 * does not move, and nothing already an amendment is touched.
 */
const adoptAmendmentsFromList = (state: FacilityScreenState, action: any) => {
  const listed = new Map<string, any>(
    (Array.isArray(action.payload) ? action.payload : [])
      .filter((a: any) => a?.id && a?.dealId?.id)
      .map((a: any) => [a.id, a]),
  )
  if (listed.size === 0) return state
  let changed = false
  const rows = state.rows.map(row => {
    if (row.amendment || row.status !== 'Approved') return row
    const saved = listed.get(row.id)
    if (!saved) return row
    changed = true
    return asAmendment(row, saved, state)
  })
  return changed ? { ...state, rows } : state
}

/**
 * The facility list read (GET_FACILITIES), or the one record a single
 * approval reloads (GET_FACILITY): an approve answers without the system id
 * the ledger assigned and without the holds, while the thunks reload the
 * list (bulk) or the record (single) anyway. Fill in what an approved row
 * still lacks from it — only blanks, never a status or a value, so a read
 * that is behind PQS cannot revert a write.
 */
const fillApprovedFromList = (state: FacilityScreenState, action: any) => {
  const approved = new Map<string, any>(
    (Array.isArray(action.payload) ? action.payload : [])
      .filter((f: any) => f?.id && f?.status === 'Approved')
      .map((f: any) => [f.id, f]),
  )
  if (approved.size === 0) return state
  let changed = false
  const rows = state.rows.map(row => {
    if (row.amendment || row.status !== 'Approved' || row.systemId) return row
    const facility = approved.get(row.id)
    if (!facility?.systemId) return row
    changed = true
    return {
      ...row,
      systemId: facility.systemId,
      holds: row.holds.length ? row.holds : (facility.holds ?? []),
    }
  })
  return changed ? { ...state, rows } : state
}

/**
 * A name list as it passes through the store — the entity list (admin names)
 * or the deal list (deal names), both loaded by the facility form when it
 * opens. Kept for the next saved facility, and filling any name a row is still
 * missing; never a status or a value, and not a write.
 */
const ingestNames =
  (kind: 'entityNames' | 'dealNames') => (state: FacilityScreenState, action: any) => {
    const list = Array.isArray(action.payload) ? action.payload : []
    const label = (x: any): string | undefined =>
      kind === 'entityNames' ? x?.entityName : x?.dealName
    const listed = list.filter((x: any) => x?.id && label(x))
    if (listed.length === 0) return state
    const names = { ...state[kind] }
    for (const x of listed) names[x.id] = label(x) as string
    let changed = false
    const rows = state.rows.map(row => {
      if (kind === 'entityNames') {
        const id = row.adminEntity?.id
        const adminEntityName = row.adminEntityName || (id && names[id]) || ''
        // the approved facility's own admin, kept on an amendment row, is named too
        const approvedId = row.approved?.adminEntity?.id
        const approved =
          row.approved && !row.approved.adminEntityName && approvedId && names[approvedId]
            ? { ...row.approved, adminEntityName: names[approvedId] }
            : row.approved
        if (adminEntityName === row.adminEntityName && approved === row.approved) return row
        changed = true
        return { ...row, adminEntityName, approved }
      }
      const id = row.dealId?.id
      if (row.dealName || !id || !names[id]) return row
      changed = true
      return { ...row, dealName: names[id] }
    })
    return { ...state, [kind]: names, rows: changed ? rows : state.rows }
  }

const facilityScreenReducer = createReducer(initialState)({
  [types.GET_FACILITY_SCREEN]: (
    state: FacilityScreenState,
    action: { payload: { rows: Row[]; generation?: number } },
  ) => {
    // Fetched before a write the store has since applied: these rows would
    // undo it. Drop them and leave the store stale; the thunk reads once more.
    if (outdated(state, action.payload.generation)) return { ...state, stale: true }
    return { ...state, rows: action.payload.rows, stale: false }
  },
  [types.START_LOADING_FACILITY_SCREEN]: (state: FacilityScreenState) => ({
    ...state,
    isLoading: true,
  }),
  [types.STOP_LOADING_FACILITY_SCREEN]: (state: FacilityScreenState) => ({
    ...state,
    isLoading: false,
  }),
  [types.FACILITY_SCREEN_LOAD_FAILED]: (state: FacilityScreenState) => ({
    ...state,
    stale: false,
  }),
  [types.INVALIDATE_FACILITY_SCREEN]: invalidate,

  // The grid reads this store, but every facility command is dispatched
  // through the facilities and facility-amendments ducks — by the row menu, by
  // the details page, by the Create New stepper. Rather than have every caller
  // remember to refresh the grid, the grid's own store listens for the same
  // successes and patches its rows — never refetches, because PQS projects
  // asynchronously and a read straight after the command can still return the
  // old contracts.
  [facilityTypes.SUBMIT_FACILITY]: (state: FacilityScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Submitted', false)(state.rows)),
  [facilityTypes.REJECT_FACILITY]: (state: FacilityScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Draft', false)(state.rows)),
  // Approval assigns the systemId, which arrives with the facility list the
  // approval thunk reloads (fillApprovedFromList).
  [facilityTypes.APPROVE_FACILITY]: (state: FacilityScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Approved', false)(state.rows)),
  [types.SET_FACILITY_SCREEN_STATUSES]: (
    state: FacilityScreenState,
    action: { payload: { ids: string[]; status: Row['status'] } },
  ) =>
    action.payload.ids.length
      ? withRows(
          state,
          withStatus(action.payload.ids, action.payload.status, false)(state.rows),
        )
      : state,
  // The delete answers per id; a row the ledger refused to delete stays.
  [facilityTypes.DELETE_FACILITY]: (state: FacilityScreenState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    return gone.size
      ? withRows(
          state,
          state.rows.filter(row => row.amendment || !gone.has(row.id)),
        )
      : state
  },
  [facilityTypes.ADD_FACILITIES]: (state: FacilityScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    return withRows(state, [draftRow(saved, state), ...state.rows])
  },
  [facilityTypes.EDIT_FACILITIES]: (state: FacilityScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    return withRows(
      state,
      state.rows.map(row =>
        !row.amendment && row.id === saved.id
          ? { ...row, ...fieldsOf(saved, state, row), status: 'Draft' }
          : row,
      ),
    )
  },
  // A lock placed or lifted. The answer carries the facility's holds as the
  // hold transaction left them (the ledger's own, not PQS's): the facility's
  // row — and the amendment standing in for it — take them in place of their
  // LendOS holds, keeping the Signing Portal's. A reload here would read PQS,
  // which may not have projected the lock yet, and would show the old row as
  // current. Without holds in the answer the grid falls back to that reload.
  [facilityTypes.FACILITY_HOLD_CHANGED]: (state: FacilityScreenState, action: any) => {
    const { facilityId, holds } = action.payload ?? {}
    if (!facilityId || !Array.isArray(holds)) return invalidate(state)
    return withRows(
      state,
      state.rows.map(row =>
        row.id === facilityId
          ? { ...row, holds: [...holds, ...row.holds.filter(h => h.system !== 'LendOS')] }
          : row,
      ),
    )
  },
  [facilityTypes.GET_FACILITIES]: fillApprovedFromList,
  // A single facility read (GET_FACILITY): the one-facility approval reloads
  // the record it approved, not the list, and that record carries the
  // systemId and holds the row still lacks.
  [facilityTypes.GET_FACILITY]: (state: FacilityScreenState, action: any) =>
    fillApprovedFromList(state, { payload: action.payload ? [action.payload] : [] }),
  [entityTypes.GET_ENTITIES]: ingestNames('entityNames'),
  [dealTypes.GET_DEALS]: ingestNames('dealNames'),

  // Amendments.
  [amendmentTypes.SUBMIT_FACILITY_AMENDMENT]: (state: FacilityScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Submitted', true)(state.rows)),
  [amendmentTypes.REJECT_FACILITY_AMENDMENT]: (state: FacilityScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Draft', true)(state.rows)),
  // Approved: the amendment's values are now the facility's. A termination
  // moves it to the Terminated grid.
  [amendmentTypes.APPROVE_FACILITY_AMENDMENT]: (state: FacilityScreenState, action: any) => {
    const [id] = oneId(action)
    if (!id) return state
    return withRows(
      state,
      state.rows.map(row =>
        row.amendment && row.id === id
          ? asApprovedFacility(row, isTermination(row) ? 'Terminated' : 'Approved')
          : row,
      ),
    )
  },
  // Deleted: the approved facility comes back with its own values, kept on
  // the row since the amendment appeared. The answer names the id the menu
  // sent: the amendment's own, or the facility's for a termination.
  [amendmentTypes.DELETE_FACILITY_AMENDMENT]: (state: FacilityScreenState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    if (!gone.size) return state
    return withRows(
      state,
      state.rows.map(row =>
        row.amendment && (gone.has(row.id) || (row.transactionId && gone.has(row.transactionId)))
          ? asRestoredFacility(row)
          : row,
      ),
    )
  },
  [amendmentTypes.ADD_FACILITY_AMENDMENTS]: applyAmendmentRecord,
  // Terminated from the row menu (utils/generation.ts): the answer carries
  // ids, not a record, and asTermination builds the row from them.
  [amendmentTypes.FACILITY_TERMINATED]: (state: FacilityScreenState, action: any) =>
    withTermination(state, action, asTermination),
  [amendmentTypes.EDIT_FACILITY_AMENDMENTS]: applyAmendmentRecord,
  [amendmentTypes.CHANGE_FACILITY_FEES_AMENDMENT]: applyAmendmentRecord,
  [amendmentTypes.CHANGE_FACILITY_AMORTIZATION_AMENDMENT]: applyAmendmentRecord,
  [amendmentTypes.GET_FACILITY_AMENDMENTS]: adoptAmendmentsFromList,
})

export default facilityScreenReducer
